// Fase 15A — Matrice Ruoli & Permessi (punti 34-37) + custom roles (38-41).
import { useCallback, useEffect, useState } from "react";
import { ShieldCheck, Plus } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export default function PlatformRoles() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ code: "", name: "", description: "", permissions: "" });
  const [profiles, setProfiles] = useState([]);
  const [profileForm, setProfileForm] = useState({
    tenant_type: "COMPANY", role: "ADMIN_AZIENDA", profile_name: "SEMPLICE",
    description: "", is_active: true });
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/admin/platform/roles/matrix");
      setData(d);
      const { data: pr } = await api.get("/admin/platform/role-profiles");
      setProfiles(pr.profiles || []);
      setError(null);
    } catch (e) {
      setError(formatApiError(e));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const createCustom = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/platform/roles/custom", {
        code: form.code, name: form.name, description: form.description,
        default_permissions: form.permissions.split(",").map((p) => p.trim()).filter(Boolean) });
      toast.success(`Custom role ${form.code.toUpperCase()} creato`);
      setForm({ code: "", name: "", description: "", permissions: "" });
      await load();
    } catch (err) {
      toast.error("Errore", { description: formatApiError(err) });
    }
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    try {
      await api.put("/admin/platform/role-profiles", profileForm);
      toast.success(`Profilo ${profileForm.profile_name} salvato per ${profileForm.role}`);
      await load();
    } catch (err) {
      toast.error("Errore", { description: formatApiError(err) });
    }
  };

  if (error) return <p className="text-sm text-red-700" data-testid="roles-error">{error}</p>;
  if (!data) return <p className="text-sm text-slate-600" data-testid="roles-loading">Caricamento…</p>;

  return (
    <div className="space-y-6" data-testid="platform-roles-page">
      <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
        <ShieldCheck className="w-7 h-7 text-green-800" aria-hidden="true" /> Ruoli & Permessi
      </h1>

      <Card className="border-slate-200 overflow-x-auto" data-testid="permission-matrix">
        <CardHeader className="pb-2"><CardTitle className="text-base">Matrice ruoli × gruppi di permessi</CardTitle></CardHeader>
        <CardContent>
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-slate-200">
                <th className="text-left py-2 pr-3 text-slate-500 font-medium">Gruppo</th>
                {data.templates.map((t) => (
                  <th key={t.code} className="text-left py-2 pr-3">
                    <span className="font-semibold text-slate-800">{t.code}</span>
                    <Badge variant="outline" className={t.system_role
                      ? "ml-1 text-[8px] bg-slate-50 text-slate-500 border-slate-300"
                      : "ml-1 text-[8px] bg-violet-50 text-violet-800 border-violet-300"}
                      data-testid={`system-badge-${t.code}`}>
                      {t.system_role ? "SYSTEM" : "CUSTOM"}</Badge>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Object.entries(data.permission_groups).map(([group, perms]) => (
                <tr key={group} className="border-b border-slate-100" data-testid={`matrix-row-${group}`}>
                  <td className="py-1.5 pr-3 font-medium text-slate-700">{group}</td>
                  {data.templates.map((t) => {
                    const granted = perms.filter((p) => t.default_permissions.includes(p));
                    const protectedHit = granted.filter((p) =>
                      (data.protected[t.code] || []).includes(p));
                    return (
                      <td key={t.code} className="py-1.5 pr-3">
                        {granted.length === 0 ? (
                          <span className="text-slate-300">—</span>
                        ) : (
                          <span className={granted.length === perms.length
                            ? "text-green-700 font-semibold" : "text-amber-700"}>
                            {granted.length}/{perms.length}
                            {protectedHit.length > 0 && (
                              <Badge variant="outline" className="ml-1 text-[8px] bg-red-50 text-red-800 border-red-300"
                                title="Permessi minimi di sistema: non rimovibili">
                                PROT</Badge>)}
                          </span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-[11px] text-slate-500 mt-3">
            I permessi marcati PROT sono il minimo di funzionamento del ruolo system e
            non possono essere rimossi senza blocco (punto 37). La modifica dei ruoli
            system è tracciata in audit con diff (punto 84).
          </p>
        </CardContent>
      </Card>

      <Card className="border-slate-200" data-testid="custom-role-card">
        <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2">
          <Plus className="w-4 h-4" aria-hidden="true" /> Custom role (scope: organizzazione)
        </CardTitle></CardHeader>
        <CardContent>
          <form onSubmit={createCustom} className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="space-y-1">
              <Label htmlFor="cr-code">Codice *</Label>
              <Input id="cr-code" required value={form.code} placeholder="REVIEWER_SR"
                data-testid="custom-role-code"
                onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="cr-name">Nome *</Label>
              <Input id="cr-name" required value={form.name} data-testid="custom-role-name"
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
            </div>
            <div className="space-y-1 md:col-span-2">
              <Label htmlFor="cr-perms">Permessi (separati da virgola)</Label>
              <Input id="cr-perms" value={form.permissions} placeholder="esg:read, audit:read"
                data-testid="custom-role-perms"
                onChange={(e) => setForm((f) => ({ ...f, permissions: e.target.value }))} />
            </div>
            <div className="md:col-span-4 flex items-center gap-3">
              <Button type="submit" className="bg-green-700 hover:bg-green-800"
                disabled={!form.code || !form.name} data-testid="custom-role-submit">
                Crea custom role</Button>
              <p className="text-[11px] text-slate-500">
                Un custom role non può mai ottenere permessi platform (punto 40).</p>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card className="border-slate-200" data-testid="role-profiles-card">
        <CardHeader className="pb-2"><CardTitle className="text-base">
          Profili ruolo (SEMPLICE / AVANZATO)
        </CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <p className="text-[11px] text-slate-500">
            Configurazione guidata per tenant e ruolo: il profilo modula l'esperienza
            (preset viste/moduli) senza toccare la matrice permessi. Se nessun profilo
            è attivo vale il ruolo base. Ogni salvataggio è tracciato in audit.
          </p>
          {profiles.length > 0 && (
            <table className="w-full text-xs" data-testid="role-profiles-table">
              <thead>
                <tr className="border-b border-slate-200 text-left text-slate-500 font-medium">
                  <th className="py-1.5 pr-3">Tenant</th><th className="py-1.5 pr-3">Ruolo</th>
                  <th className="py-1.5 pr-3">Profilo</th><th className="py-1.5 pr-3">Attivo</th>
                  <th className="py-1.5">Descrizione</th>
                </tr>
              </thead>
              <tbody>
                {profiles.map((pr) => (
                  <tr key={`${pr.tenant_type}-${pr.role}-${pr.profile_name}`}
                    className="border-b border-slate-100"
                    data-testid={`profile-row-${pr.tenant_type}-${pr.role}-${pr.profile_name}`}>
                    <td className="py-1.5 pr-3">{pr.tenant_type}</td>
                    <td className="py-1.5 pr-3 font-medium">{pr.role}</td>
                    <td className="py-1.5 pr-3">{pr.profile_name}</td>
                    <td className="py-1.5 pr-3">{pr.is_active ? "Sì" : "No"}</td>
                    <td className="py-1.5 text-slate-600">{pr.description || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <form onSubmit={saveProfile} className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <div className="space-y-1">
              <Label htmlFor="rp-tenant">Tenant</Label>
              <Select value={profileForm.tenant_type}
                onValueChange={(v) => setProfileForm((f) => ({ ...f, tenant_type: v }))}>
                <SelectTrigger id="rp-tenant" data-testid="profile-tenant-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="COMPANY" data-testid="profile-tenant-company">Azienda</SelectItem>
                  <SelectItem value="CONSULTANT" data-testid="profile-tenant-consultant">Consulente</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="rp-role">Ruolo</Label>
              <Input id="rp-role" required value={profileForm.role} data-testid="profile-role-input"
                onChange={(e) => setProfileForm((f) => ({ ...f, role: e.target.value }))} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="rp-name">Profilo</Label>
              <Select value={profileForm.profile_name}
                onValueChange={(v) => setProfileForm((f) => ({ ...f, profile_name: v }))}>
                <SelectTrigger id="rp-name" data-testid="profile-name-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="SEMPLICE" data-testid="profile-name-semplice">SEMPLICE</SelectItem>
                  <SelectItem value="AVANZATO" data-testid="profile-name-avanzato">AVANZATO</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1 md:col-span-2">
              <Label htmlFor="rp-desc">Descrizione</Label>
              <Input id="rp-desc" value={profileForm.description} data-testid="profile-desc-input"
                onChange={(e) => setProfileForm((f) => ({ ...f, description: e.target.value }))} />
            </div>
            <div className="md:col-span-5">
              <Button type="submit" className="bg-green-700 hover:bg-green-800"
                disabled={!profileForm.role} data-testid="profile-save">
                Salva profilo</Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
